/**
 * AJOUTER DES ARTICLES — la fenêtre du studio (maquette `artM`) : une recherche et des filtres qui s'additionnent, des
 * cases à cocher, « + » pour un article seul. Un article DÉJÀ dans le niveau visé est signalé et ignoré à l'ajout :
 * deux tuiles identiques côte à côte ne servent personne.
 *
 * ⚠️ LA RECHERCHE VIT AU SERVEUR, BORNÉE ET DITE : le navigateur ne tient pas le catalogue (des dizaines de milliers de
 *    déclinaisons), donc « 60 premiers affichés » se dit plutôt que de laisser croire que la liste est complète.
 * ⚠️ UNE DÉCLINAISON, PAS UN MODÈLE : la caisse vend une taille et un coloris précis, et la tuile porte l'article
 *    exact — c'est pourquoi la taille et le coloris se lisent sur chaque ligne.
 * ⚠️ LE PANNEAU DE LA MAQUETTE, PAS LA MODALE DE LA CHARTE : un panneau de 640 px sans tuile d'icône, la recherche dans
 *    un bandeau, le pied sur fond gris — posé dans le voile partagé, le clavier tenu par `useOverlayPanel`.
 */
import { useEffect, useId, useMemo, useState } from 'react';
import { Icon, ModalBackdrop, useOverlayPanel } from '@orgazone/ui-web';
import { api, type StudioArticle, type StudioCandidate } from '../api';
import { t } from '../i18n';
import { fieldLabel } from '../vocabulary';
import { formatPrice } from './quick-studio-model';

const MONO = 'var(--font-mono)';
/** Délai avant de chercher — une recherche par frappe saturerait le serveur pour des résultats aussitôt remplacés. */
const SEARCH_DELAY_MS = 180;

export function QuickStudioArticles({ title, present, onAdd, onClose }: {
  title: string;
  /** Les articles déjà dans le niveau visé — ni cochables, ni ajoutables, et c'est dit. */
  present: ReadonlySet<string>;
  onAdd: (articles: StudioArticle[]) => void;
  onClose: () => void;
}) {
  const [query, setQuery] = useState('');
  const [sector, setSector] = useState('');
  const [family, setFamily] = useState('');
  const [brand, setBrand] = useState('');
  const [result, setResult] = useState<{ rows: StudioCandidate[]; truncated: boolean; facets: { sectors: string[]; families: string[]; brands: string[] } } | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [searching, setSearching] = useState(false);
  const [checked, setChecked] = useState<Map<string, StudioArticle>>(() => new Map());

  // ⚠️ AUCUNE LIGNE SANS CRITÈRE : lister le catalogue entier par ordre alphabétique ne montrerait que ses premières
  //    lettres, et ferait croire à un choix complet. Les FILTRES, eux, sont servis d'emblée — on peut commencer par un
  //    rayon — et c'est le serveur qui décide qu'une requête sans critère ne rend pas de ligne.
  const asked = query.trim().length >= 2 || !!sector || !!family || !!brand;
  useEffect(() => {
    let alive = true;
    setSearching(true);
    const timer = setTimeout(() => {
      api.quickStudioSearch({ q: query.trim(), sector, family, brand })
        .then((r) => { if (alive) { setResult(r); setFailure(null); } })
        .catch((e: unknown) => { if (alive) { setResult(null); setFailure(e instanceof Error ? e.message : String(e)); } })
        .finally(() => { if (alive) setSearching(false); });
    }, SEARCH_DELAY_MS);
    return () => { alive = false; clearTimeout(timer); };
  }, [query, sector, family, brand]);

  const rows = useMemo(() => result?.rows ?? [], [result]);
  const free = useMemo(() => rows.filter((r) => !present.has(r.id)), [rows, present]);
  const allChecked = free.length > 0 && free.every((r) => checked.has(r.id));
  const toggle = (r: StudioCandidate): void => setChecked((prev) => {
    const next = new Map(prev);
    if (!next.delete(r.id)) next.set(r.id, r);
    return next;
  });
  const toggleAll = (): void => setChecked((prev) => {
    const next = new Map(prev);
    for (const r of free) { if (allChecked) next.delete(r.id); else next.set(r.id, r); }
    return next;
  });
  const reset = (): void => { setQuery(''); setSector(''); setFamily(''); setBrand(''); };

  const selectStyle = {
    height: 32, padding: '0 9px', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-xs)',
    background: 'var(--paper)', fontSize: 'var(--t-body)', color: 'var(--ink-800)', minWidth: 0, flex: '1 1 150px',
  } as const;
  const titleId = useId();
  const { panelRef } = useOverlayPanel({ open: true, onClose });

  return (
    <ModalBackdrop onBackdrop={onClose}>
      <div ref={panelRef} role="dialog" aria-modal aria-labelledby={titleId} tabIndex={-1}
        style={{ position: 'relative', width: 640, maxWidth: '96vw', height: 'min(640px, 86vh)', display: 'flex', flexDirection: 'column', background: 'var(--paper)', borderRadius: 'var(--r-md)', boxShadow: '0 28px 70px -12px rgba(0,0,0,.45)', overflow: 'hidden' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '15px 18px', borderBottom: '1px solid var(--ink-200)' }}>
        <span style={{ flex: 1, minWidth: 0 }}>
          <span id={titleId} style={{ display: 'block', fontSize: 15.5, fontWeight: 700, color: 'var(--ink-900)' }}>{title}</span>
          <span style={{ display: 'block', fontSize: 'var(--t-dense)', color: 'var(--ink-500)', marginTop: 2, lineHeight: 1.45 }}>
            {t('« + » ajoute un article seul · cochez-en plusieurs pour les ajouter d’un coup.')}
          </span>
        </span>
        <button type="button" onClick={onClose} aria-label={t('Fermer')}
          style={{ width: 30, height: 30, flexShrink: 0, border: 'none', borderRadius: 'var(--r-xs)', background: 'var(--ink-100)', color: 'var(--ink-600)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: 0 }}>
          <Icon name="x" size={14} stroke={2.4} />
        </button>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 9, padding: '11px 18px', borderBottom: '1px solid var(--ink-100)', background: 'var(--ink-50)' }}>
        <input
          value={query} onChange={(e) => setQuery(e.target.value)} autoFocus className="oz-qs-source-search"
          placeholder={t('Nom, code article, code-barres…')} aria-label={t('Nom, code article, code-barres…')}
          style={{ width: '100%', height: 36, padding: '0 11px', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-xs)', background: 'var(--paper)', fontSize: 'var(--t-lead)', color: 'var(--ink-900)', outline: 'none', boxSizing: 'border-box' }}
        />
        {(result?.facets.sectors.length ?? 0) > 0 && (
          <span style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {['', ...result!.facets.sectors].map((v) => {
              const on = sector === v;
              return (
                <button
                  key={v || '*'} type="button" onClick={() => { setSector(v); setFamily(''); setBrand(''); }}
                  style={{
                    height: 28, padding: '0 11px', borderRadius: 'var(--r-pill)', fontSize: 'var(--t-dense)', fontWeight: 600, whiteSpace: 'nowrap',
                    border: `1.5px solid ${on ? 'var(--teal-400)' : 'var(--ink-200)'}`,
                    background: on ? 'var(--teal-100)' : 'var(--paper)', color: on ? 'var(--teal-800)' : 'var(--ink-600)',
                  }}
                >{v || t('Tout')}</button>
              );
            })}
          </span>
        )}
        <span style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <select value={family} onChange={(e) => { setFamily(e.target.value); setBrand(''); }} aria-label={fieldLabel('family')} style={selectStyle}>
            <option value="">{t('{champ} : tout', { champ: fieldLabel('family') })}</option>
            {(result?.facets.families ?? []).map((v) => <option key={v} value={v}>{v}</option>)}
          </select>
          <select value={brand} onChange={(e) => setBrand(e.target.value)} aria-label={fieldLabel('brand')} style={selectStyle}>
            <option value="">{t('{champ} : tout', { champ: fieldLabel('brand') })}</option>
            {(result?.facets.brands ?? []).map((v) => <option key={v} value={v}>{v}</option>)}
          </select>
          {asked && (
            <button type="button" onClick={reset} className="oz-qs-art-reset" style={{ height: 32, padding: '0 10px', border: 'none', borderRadius: 'var(--r-xs)', fontWeight: 600, fontSize: 'var(--t-dense)', whiteSpace: 'nowrap' }}>
              {t('Effacer')}
            </button>
          )}
        </span>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 18px', borderBottom: '1px solid var(--ink-100)' }}>
        {free.length > 0 && (
          <button type="button" onClick={toggleAll} style={{ display: 'inline-flex', alignItems: 'center', gap: 8, border: 'none', background: 'transparent', padding: 0, fontSize: 'var(--t-dense)', fontWeight: 600, color: 'var(--ink-600)' }}>
            <CheckBox on={allChecked} />
            {allChecked ? t('Tout décocher') : t('Cocher les {n} affichés', { n: free.length })}
          </button>
        )}
        <span style={{ marginLeft: 'auto', fontSize: 'var(--t-note)', color: 'var(--ink-400)', fontVariantNumeric: 'tabular-nums' }}>
          {searching ? t('Recherche…') : result && asked
            ? (result.truncated ? t('{n} premiers affichés — affinez la recherche', { n: rows.length }) : t('{n} articles', { n: rows.length }))
            : ''}
        </span>
      </div>

      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '4px 10px' }}>
        {!asked && !failure && (
          <div style={{ padding: '26px 10px', textAlign: 'center', fontSize: 'var(--t-body)', color: 'var(--ink-400)' }}>
            {t('Tapez un nom ou un code, ou choisissez un filtre.')}
          </div>
        )}
        {/* L'échec se dit même sans critère : ce sont alors les FILTRES qui n'ont pas pu être lus. */}
        {failure && (
          <div role="alert" style={{ padding: '18px 10px', textAlign: 'center', fontSize: 'var(--t-body)', color: 'var(--rose-fg)' }}>
            {t('La recherche a échoué : {motif}', { motif: failure })}
          </div>
        )}
        {asked && !failure && result && rows.length === 0 && (
          <div style={{ padding: '26px 10px', textAlign: 'center', fontSize: 'var(--t-body)', color: 'var(--ink-400)' }}>{t('Aucun article ne correspond.')}</div>
        )}
        {rows.map((r) => {
          const has = present.has(r.id);
          const on = !has && checked.has(r.id);
          const variant = [r.size, r.color].filter(Boolean).join(' · ');
          const label = variant ? `${r.name} — ${variant}` : r.name;
          return (
            <div
              key={r.id} onClick={() => { if (!has) toggle(r); }} className={on ? undefined : 'oz-qs-art-row'}
              style={{
                display: 'flex', alignItems: 'center', gap: 11, padding: 8, borderRadius: 'var(--r-xs)',
                background: on ? 'var(--teal-100)' : 'transparent', opacity: has ? 0.5 : 1, cursor: has ? 'default' : 'pointer',
              }}
            >
              {/* ⚠️ LA CASE EST UN VRAI CONTRÔLE : la ligne se coche au clic n'importe où, mais un `div` cliquable n'existe
                  pas au clavier — et le « + » de la ligne AJOUTE, il ne coche pas. Sans elle, cocher plusieurs articles
                  était réservé à la souris. Elle porte son geste — vivre par la remontée jusqu'à la ligne serait un
                  bouton mort en sursis. */}
              {has ? <Icon name="check" size={17} stroke={2.6} color="var(--teal-600)" style={{ flexShrink: 0 }} /> : (
                <button
                  type="button" role="checkbox" aria-checked={on} aria-label={label}
                  onClick={(e) => { e.stopPropagation(); toggle(r); }}
                  style={{ display: 'inline-flex', padding: 0, border: 'none', background: 'transparent', cursor: 'pointer' }}
                >
                  <CheckBox on={on} />
                </button>
              )}
              <span style={{ flex: 1, minWidth: 0 }}>
                <span style={{ display: 'block', fontSize: 'var(--t-lead)', fontWeight: 600, color: 'var(--ink-900)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {r.name}{variant ? <span style={{ fontWeight: 500, color: 'var(--ink-500)' }}> — {variant}</span> : null}
                </span>
                <span style={{ display: 'block', fontSize: 'var(--t-meta)', color: 'var(--ink-500)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  <span style={{ fontFamily: MONO }}>{r.gencode}</span>{r.family ? ` · ${r.family}` : ''}{r.brand ? ` · ${r.brand}` : ''}
                </span>
              </span>
              <span style={{ fontSize: 'var(--t-body)', fontWeight: 700, color: 'var(--ink-800)', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{formatPrice(r.priceCents)}</span>
              {has
                ? <span style={{ fontSize: 'var(--t-meta)', color: 'var(--ink-400)', whiteSpace: 'nowrap' }}>{t('déjà là')}</span>
                : (
                  <button
                    type="button" aria-label={t('Ajouter « {v} » seul', { v: label })} title={t('Ajouter cet article seul')}
                    onClick={(e) => { e.stopPropagation(); onAdd([r]); }}
                    className="oz-qs-art-one"
                    style={{ width: 28, height: 28, flexShrink: 0, padding: 0, borderRadius: 'var(--r-xs)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
                  >
                    <Icon name="plus" size={13} stroke={2.4} />
                  </button>
                )}
            </div>
          );
        })}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '12px 18px', borderTop: '1px solid var(--ink-100)', background: 'var(--ink-50)' }}>
        <button type="button" onClick={onClose}
          style={{ height: 36, padding: '0 13px', border: 'none', borderRadius: 'var(--r-xs)', background: 'transparent', color: 'var(--ink-600)', fontSize: 'var(--t-lead)', fontWeight: 600 }}>
          {t('Annuler')}
        </button>
        <span style={{ flex: 1 }} />
        {/* Inactif tant que rien n'est coché : le gris de la maquette, et le mot dit ce qu'il attend. */}
        <button type="button" disabled={checked.size === 0} onClick={() => onAdd([...checked.values()])}
          style={{ height: 36, padding: '0 16px', border: 'none', borderRadius: 'var(--r-xs)', background: checked.size ? 'var(--ink-900)' : 'var(--ink-200)', color: checked.size ? 'var(--paper)' : 'var(--ink-400)', fontSize: 'var(--t-lead)', fontWeight: 700 }}>
          {checked.size === 0 ? t('Cochez des articles') : t('Ajouter {n} articles', { n: checked.size })}
        </button>
      </div>
      </div>
    </ModalBackdrop>
  );
}

/** La case à cocher de la charte — dessinée, parce qu'elle vit dans une ligne entière qui se coche au clic. */
function CheckBox({ on }: { on: boolean }) {
  return (
    <span
      aria-hidden
      style={{
        width: 17, height: 17, borderRadius: 5, flexShrink: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        border: `1.5px solid ${on ? 'var(--teal-500)' : 'var(--ink-300)'}`, background: on ? 'var(--teal-500)' : 'var(--paper)', color: 'var(--paper)',
      }}
    >
      {on && <Icon name="check" size={11} />}
    </span>
  );
}
